import {ChangeDetectionStrategy, Component, EventEmitter, HostListener, Inject, OnInit, Output} from "@angular/core";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {PlanningFeaturePhaseEnum} from "../../../models/planning-feature-phase-enum";

interface Package {
  id: string;
  barcode: string;
  weight: number;
  pallets: number;
  volume: number;
  dimensions: {
    length: number;
    width: number;
    height: number;
    cube: number;
  }
  count: number;
  originalCount: number
  selected?: boolean;
  size: string;
}

require('./planning-draft-route-job-split-by-packages.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-route-job-split-by-packages',
  templateUrl: './planning-draft-route-job-split-by-packages.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRouteJobSplitByPackagesComponent implements OnInit {

  public job: any;
  private _resources: any[];
  public sitePackageItemsMap: any;

  @Output() onClose = new EventEmitter();
  @Output() onSplitJobByPackages = new EventEmitter<{
    packages: Package[], resourceId?: number, routeId?: number, optimize: boolean
  }>();

  jobId: string;
  selectedPackages: Package[] = [];
  optimize: boolean = true;
  selectedTotals: { weight: number, volume: number, pallets: number } = {weight: 0, volume: 0, pallets: 0};
  selectedResource: any;
  packages: Package[] = [];
  FeaturePhase = PlanningFeaturePhaseEnum;
  routeTotals: { weight: number, volume: number, pallets: number } = {weight: 0, volume: 0, pallets: 0};
  resourcesToShow: any[] = [];
  currentPhase = PlanningFeaturePhaseEnum.SPLIT;
  lastSelectedPackage: { id: string; index: number } = { id: '', index: -1 };
  isShiftPressed: boolean = false;

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.shiftKey) {
      this.isShiftPressed = true;
    }
  }

  @HostListener('keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (!event.shiftKey) {
      this.isShiftPressed = false;
    }
  }

  constructor(
    @Inject(PANEL_DATA) public data: { job: any, resources: any[] },
    @Inject('splitService') private splitService
  ) {
  }

  ngOnInit() {
    this.sitePackageItemsMap = this.splitService.getSitePackageItemsMap();

    this.job = this.data.job;
    this._resources = this.data.resources;
    this.resourcesToShow = [...this._resources];
    this.jobId = this.job.externalId || this.job.orderId || this.job.fulfillmentStepId;
    this._initializePackages();
  }

  onPackageSelect(pkg: Package, index: number) {
    pkg.selected = !pkg.selected;

    if (pkg.selected && !pkg.count) {
      pkg.count = pkg.originalCount;
    }

    if (this.lastSelectedPackage.id && this.isShiftPressed) {
      const previousIndex = this.lastSelectedPackage.index;

      if (previousIndex > -1) {
        const start: number = index > previousIndex ? previousIndex : index;
        const end: number = index > previousIndex ? index : previousIndex;

        for (let i = start; i <= end; i++) {
          this.packages[i].selected = pkg.selected;

          if (this.packages[i].selected && !this.packages[i].count) {
            this.packages[i].count = this.packages[i].originalCount;
          }
        }
      }
    }

    this.lastSelectedPackage.id = pkg.id;
    this.lastSelectedPackage.index = index;
    this._updateSelectedPackages();
  }

  trackByPackageId(index: number, pkg: Package): string {
    return pkg.id;
  }

  trackByResourceId(index: number, resource: any): string {
    return resource.id;
  }

  onCountChange(newValue: number, pkg: Package, index: number) {
    this.packages[index] = {...pkg, count: newValue};
    this._updateSelectedPackages();
  }

  onResourceSelect(event: any) {
    this.selectedResource = event;
  }

  close() {
    this.onClose.emit();
  }

  split() {
    if (!this.canSplit()) return;
    this.onSplitJobByPackages.emit({
      packages: this.selectedPackages,
      routeId: null,
      resourceId: null,
      optimize: this.optimize
    });
    this.onClose.emit();
  }

  assign() {
    if (!this.selectedResource) return;
    this.onSplitJobByPackages.emit({
      packages: this.selectedPackages,
      resourceId: this.selectedResource?.id,
      routeId: this.selectedResource?.route?.id,
      optimize: this.optimize
    });
    this.onClose.emit();
  }

  setFeaturePhase(featurePhase: PlanningFeaturePhaseEnum) {
    if (this.currentPhase === this.FeaturePhase.SPLIT && !this.canSplit()) return;
    this.selectedResource = null;
    this.currentPhase = featurePhase
  }

  onQueryChange(query: string = '') {
    this.resourcesToShow = this._resources.filter(resource =>
      resource.vehicleName.toLowerCase().includes(query.toLowerCase()) ||
      resource?.routeName?.toLowerCase().includes(query.toLowerCase())
    );
  }

  isPackageInvalid(pkg: Package): boolean {
    return pkg.selected && (
      pkg.count > pkg.originalCount ||
      (pkg.originalCount === 1 && this.packages.length === 1) ||
      (pkg.count === pkg.originalCount && this.packages.length === 1)
    );
  }

  canSplit() {
    if (this.selectedPackages.length === 0) {
      return false;
    }

    if (this.selectedPackages.some(pkg => pkg.count > pkg.originalCount)) {
      return false;
    }

    // Special case if there's a single package - check if originalCount = 1
    if (this.packages.length === 1 &&
      this.selectedPackages.length === 1 &&
      this.selectedPackages[0].originalCount === 1) {
      return false;
    }
    // Special case if there's a single package - check if originalCount = count
    if (this.packages.length === 1 &&
      this.selectedPackages.length === 1 &&
      this.selectedPackages[0].originalCount === this.selectedPackages[0].count) {
      return false;
    }

    return true;
  }

  private _initializePackages() {
    if (this.job?.packages) {
      this.packages = this.job.packages
        .filter(pkg => pkg.id)
        .map((pkg: any) => ({
          ...pkg,
          selected: false,
          originalCount: pkg.count,
          barcode: pkg.barcode || '',
        }));
    }
  }

  calculateUpdatedDetails(resource): { weightPercentage: number, volumePercentage: number, pallets: number } {
    // Update details:
    // 1. New weight = (RouteTotals.weight + selectedTotals.weight) / resource.capacity.weight
    // 2. New volume = (RouteTotals.volume + selectedTotals.volume) / resource.capacity.volume
    // 3. New pallets = RouteTotals.pallets + selectedTotals.pallets
    let totals = {...this.selectedTotals};
    const routeTotals = resource.routeTotals;

    if (routeTotals) {
      totals.weight += routeTotals.totalLoad;
      totals.volume += routeTotals.totalVolume;
      totals.pallets += routeTotals.totalPallets;
    }

    const resourceVolume = resource.capacity.dimensions ?
      resource.capacity.dimensions.length * resource.capacity.dimensions.width * resource.capacity.dimensions.height :
      0;

    return {
      weightPercentage: totals.weight / resource.capacity.weight,
      volumePercentage: resourceVolume ? totals.volume / resourceVolume : 0,
      pallets: totals.pallets,
    };
  }

  private _updateSelectedPackages() {
    this.selectedPackages = this.packages.filter(pkg => pkg.selected);
    this._updateSelectedTotals();
  }

  private _updateSelectedTotals() {
    let weight = 0, volume = 0, pallets = 0;
    const isTotalWeight = this.splitService.getPackageWeightIsTotal();
    const isTotalVolume = this.splitService.getPackageVolumeIsTotal();

    this.selectedPackages.forEach(pkg => {
      weight += pkg.weight || 0;
      volume += (pkg.volume ? pkg.volume : pkg.dimensions?.cube) || 0;
      pallets += pkg.pallets || 0;

      if (!isTotalWeight) {
        weight *= pkg.count;
      }

      if (!isTotalVolume) {
        volume *= pkg.count;
      }
    });
    this.selectedTotals = {weight, volume, pallets};
  }
}
